/**
 * Turning a stored `projects.vercel_url` into something safe to iframe, link, or
 * probe (GT-02).
 *
 * WHY THIS EXISTS
 * `projects.vercel_url` is not guaranteed to carry a scheme. The publish path
 * writes an absolute `https://<repo>.panda.new` today, but rows predating that
 * derivation — and the seed/imported builds that make up the most-remixed end of
 * the gallery — hold a bare host. Every other consumer already normalizes on
 * read (app/admin/projects/[id], build-quality-survey, growth/tiktok, …); the
 * community gallery was the one that did not, and a bare host is the single
 * cheapest way to manufacture a 404:
 *
 *     <iframe src="my-app.panda.new">  on  https://panda.new/community
 *       → resolves RELATIVE → https://panda.new/community/my-app.panda.new
 *       → our own 404 page, rendered inside our own preview modal.
 *
 * So normalization is not cosmetic here — it is one of the two ways the preview
 * 404s. The other (the deployment itself being gone) is what
 * app/api/community-projects/[id]/preview-health handles.
 *
 * Pure and isomorphic: the modal normalizes before setting `src`, the route
 * normalizes before fetching, and the unit test exercises it directly.
 */

/**
 * Hostnames that must never be fetched by the server-side probe. The URL always
 * comes from a DB row rather than from the caller, so this is defense in depth
 * rather than the primary control — but the probe is a PUBLIC, unauthenticated
 * endpoint, and "an admin pasted a localhost URL into a row" should degrade to
 * "no preview" rather than to a request against our own metadata service.
 *
 * Matched on the literal hostname only. We deliberately do NOT resolve DNS: a
 * resolve-then-fetch pair is its own TOCTOU rebinding hole, and the real control
 * is that the value is ours, not the caller's.
 */
const BLOCKED_HOST_RE =
  /^(localhost|.*\.localhost|127\.\d+\.\d+\.\d+|0\.0\.0\.0|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+|169\.254\.\d+\.\d+|\[::1?\]|\[fc[0-9a-f]{2}:.*\]|\[fd[0-9a-f]{2}:.*\]|.*\.internal|.*\.local)$/i;

/**
 * Normalize a stored deploy URL into an absolute `http(s)` URL, or null when the
 * value cannot be one.
 *
 * Returns null (rather than a best guess) for anything that isn't a real public
 * page: an empty/whitespace value, a non-http scheme (`javascript:`, `data:`,
 * `file:` — all of which would be actively dangerous as an iframe `src`), a host
 * that isn't publicly routable, or a string the URL parser rejects outright.
 * Callers treat null as "this project has no live preview", which is exactly the
 * empty state the modal already renders.
 */
export function normalizeCommunityPreviewUrl(
  raw: string | null | undefined,
): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // A bare host is the common legacy shape. Anything carrying an explicit scheme
  // is left alone so a non-http scheme is REJECTED below rather than silently
  // rewritten into an https URL it never was.
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (!url.hostname) return null;
  if (BLOCKED_HOST_RE.test(url.hostname)) return null;
  // A host with no dot is either a bare word ("undefined", "null", a repo name
  // that lost its suffix) or an intranet name — never a public deploy.
  if (!url.hostname.includes(".")) return null;

  return url.toString();
}
